import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { EntidadePaginadaTabelas } from '@inovamobil/icm-tabelas';

import { IRetornoBotao } from './interface/icm-retorno-botao';
import { IcmServiceTabelaGenerica } from './model/icm-service-generico.model';

@Component({
    selector: 'icm-data-table',
    templateUrl: './view/icm-data-table.component.html',
    styleUrls: ['./view/icm-data-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition(
                'expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            ),
        ]),
    ],
})
export class IcmDatatableComponent implements OnInit, OnChanges, OnDestroy {
    // Obrigatorios
    @Input() service: IcmServiceTabelaGenerica<any, any>;
    @Input() tituloConteudoVazio: string = 'Nenhum resultado encontrado!';
    @Input() textoConteudoVazio: string =
        'Não conseguimos encontrar o que você está procurando. Por favor tente de outra maneira.';
    @Input() carregando: boolean = false;

    @Input() titulo?: string;
    @Input() icone?: string;

    // Com Filtro
    private _togglePesquisar: boolean = false;
    @Input() existeFiltro?: boolean = true;
    @Output() pesquisarEvent = new EventEmitter<EntidadePaginadaTabelas<any, any>>();

    // Com Seleção
    @Input() ehSelecionavel?: boolean = true;
    @Input() tituloBotaoAcao?: string;
    @Input() iconeBotaoAcao?: string;
    @Input() exibirBotaoSelecao?: boolean = true;
    @Input() desabilitarBotaoItensSelecionados?: boolean;
    @Input() textoQuantidadeSelecionada?: string;
    @Output() aoClicarBotaoAcao = new EventEmitter<void>();
    @Output() selecaoAlterada = new EventEmitter<void>();

    // Com Paginação
    readonly menuQuantidadePorPagina = [10, 50, 100, 200, 500];
    @Input() existePaginacao?: boolean = true;
    @Input() esconderMenuQuantidadePorPagina?: boolean = false;

    // Com Atualização
    @Input() exibirBotaoAtualizar?: boolean = false;

    @Input() alturaMaxima?: string = '40vh';

    @Input() templateRef;

    @ViewChild(MatSort) matSort: MatSort;

    linhaExpandida;
    idBotaoExpandido: number;

    private _colunasParaExibir: string[] = [];
    private _configuracoesColunas: any[] = [];

    get colunasParaExibir() {
        return this._colunasParaExibir;
    }

    set colunasParaExibir(colunas: string[]) {
        this._colunasParaExibir = colunas;
    }

    get configuracoesColunas() {
        return this._configuracoesColunas;
    }

    set configuracoesColunas(configColunas: any[]) {
        this._configuracoesColunas = configColunas;
    }

    get togglePesquisar() {
        return this._togglePesquisar;
    }

    set togglePesquisar(exibir: boolean) {
        this._togglePesquisar = exibir;
    }

    constructor() {}

    ngOnInit(): void {
        this._definirColunas();
    }

    ngOnDestroy(): void {
        this.service.limparTabela();
        this.service.desmarcarTodos();
        this.service.termo = '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.ehSelecionavel && !changes.ehSelecionavel.isFirstChange()) {
            this.service.desmarcarTodos();
            this._definirColunas();
        }

        if (changes?.carregando?.currentValue) {
            this.expandirOuRecolher({
                id: this.idBotaoExpandido,
                linha: this.linhaExpandida,
            });
        }
    }

    private _definirColunas() {
        this.configuracoesColunas = this.service.configuracoesColunas;
        this.colunasParaExibir = [];

        this.configuracoesColunas.map((col) => {
            this.colunasParaExibir.push(col.identificadorColuna);
        });
        if (this.ehSelecionavel) this.colunasParaExibir.unshift('selecionar');
    }

    atualizarDados(): void {
        this.pesquisarEvent.emit();
    }

    paginar(paginator: MatPaginator): void {
        const posicao = paginator.pageIndex + 1;
        const quantidade = paginator.pageSize;

        this.service.atualizarFiltroEPosicao(posicao);
        this.service.atualizarQuantidadeItensPagina(quantidade);
        this.pesquisarEvent.emit();
    }

    pesquisarComFiltro(termo: string): void {
        this.service.termo = termo;
        this.service.atualizarFiltroEPosicao();
        this.pesquisarEvent.emit();
    }

    ordenar(event: MatSort): void {
        let campoOrdenacao = event.active;

        if (campoOrdenacao) {
            const primeiraLetra: string = event.active.charAt(0);
            campoOrdenacao = primeiraLetra.toUpperCase() + event.active.slice(1);
        }

        this.service.campoOrdenacao = campoOrdenacao;
        this.service.tipoOrdenacao = event.direction;
        this.service.atualizarFiltroEPosicao();
        this.pesquisarEvent.emit();
    }

    expandirOuRecolher({ id, linha }: IRetornoBotao<any>): void {
        const jaExpandido = this.idBotaoExpandido === id && this.linhaExpandida === linha;
        this.idBotaoExpandido = jaExpandido ? null : id;
        this.linhaExpandida = linha;
    }

    aoClicarLinha(linha) {
        if (this.linhaExpandida === linha) {
            this.expandirOuRecolher({ id: this.idBotaoExpandido, linha });
        }
    }

    selecionarTodos() {
        this.service.selecionarTodos();
        this.selecaoAlterada.emit();
    }

    selecionarTodasLinhas() {
        this.service.selecionarTodasLinhas();
        this.selecaoAlterada.emit();
    }

    selecionarTodosDaPagina() {
        this.service.selecionarTodosDaPagina();
        this.selecaoAlterada.emit();
    }

    desmarcarTodos() {
        this.service.desmarcarTodos();
        this.selecaoAlterada.emit();
    }

    selecionarLinha(linha) {
        this.service.selecionarLinha(linha);
        this.selecaoAlterada.emit();
    }
}
